import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { derivarAcento, derivarResalte, hojaDeAcento, hojaDeResalte } from '../estilos/acento';
import { MARCA_POR_DEFECTO, construirTema, type ModoDeTema } from './tema';
import type { Marca } from './tipos';

interface ValorContexto {
  marca: Marca;
  cargando: boolean;
}

const Contexto = createContext<ValorContexto>({ marca: MARCA_POR_DEFECTO, cargando: false });

/** Profundidad de anidamiento: la marca mas interna es la que se ve. */
const Profundidad = createContext(0);

export function useMarca(): ValorContexto {
  return useContext(Contexto);
}

/**
 * Registro de acentos montados.
 *
 * El acento se declara en `:root` (y no en un envoltorio) porque los menus y
 * dialogos de MUI se pintan en un portal, fuera del arbol del proveedor. Como
 * hay proveedores anidados —la pantalla de acceso y el portal de citas pintan
 * la marca del consultorio del enlace dentro del proveedor general—, gana el
 * mas profundo; a igual profundidad, el ultimo montado.
 */
const ID_HOJA = 'acento-de-marca';
const registro = new Map<number, { profundidad: number; hoja: string }>();
let siguienteId = 0;

function aplicarAcento() {
  let elegido: { id: number; profundidad: number; hoja: string } | null = null;
  for (const [id, entrada] of registro) {
    if (
      !elegido ||
      entrada.profundidad > elegido.profundidad ||
      (entrada.profundidad === elegido.profundidad && id > elegido.id)
    ) {
      elegido = { id, ...entrada };
    }
  }
  let estilo = document.getElementById(ID_HOJA) as HTMLStyleElement | null;
  if (!elegido) {
    estilo?.remove();
    return;
  }
  if (!estilo) {
    estilo = document.createElement('style');
    estilo.id = ID_HOJA;
  }
  // Siempre al final del <head>: a igual especificidad, gana a los respaldos
  // neutros de `semantico.css`.
  document.head.appendChild(estilo);
  if (estilo.textContent !== elegido.hoja) estilo.textContent = elegido.hoja;
}

interface Props {
  marca?: Marca | null;
  cargando?: boolean;
  /** Claro u oscuro, ya resuelto. Es preferencia del dispositivo, no del consultorio. */
  modo?: ModoDeTema;
  children: ReactNode;
}

/**
 * Aplica la identidad del consultorio: acento derivado de su color, tema de
 * MUI y titulo del documento.
 *
 * El acento se calcula con `derivarAcento` (luminosidad ajustada para AA) y se
 * inyecta como variables `--accent-*` en `:root`, para claro y para oscuro.
 * Mientras no se conoce la marca se usa la neutral, nunca la de un cliente.
 */
export function ProveedorDeMarca({ marca, cargando = false, modo = 'claro', children }: Props) {
  const efectiva = marca ?? MARCA_POR_DEFECTO;
  const profundidad = useContext(Profundidad) + 1;
  const { colorPrimario, colorSecundario } = efectiva;

  const acento = useMemo(() => derivarAcento(colorPrimario), [colorPrimario]);
  const hoja = useMemo(
    () => hojaDeAcento(acento) + hojaDeResalte(derivarResalte(colorSecundario)),
    [acento, colorSecundario],
  );

  // El tema depende solo de los colores y del modo, no de la identidad del
  // objeto `marca` (que el llamador puede recrear en cada render).
  const tema = useMemo(
    () => construirTema({ ...MARCA_POR_DEFECTO, colorPrimario, colorSecundario }, modo, acento),
    [colorPrimario, colorSecundario, modo, acento],
  );

  // Antes de pintar, para que el acento y la paleta de MUI lleguen juntos.
  useLayoutEffect(() => {
    const id = ++siguienteId;
    registro.set(id, { profundidad, hoja });
    aplicarAcento();
    return () => {
      registro.delete(id);
      aplicarAcento();
    };
  }, [hoja, profundidad]);

  useEffect(() => {
    document.title = efectiva.nombreComercial;
  }, [efectiva.nombreComercial]);

  const valor = useMemo(() => ({ marca: efectiva, cargando }), [efectiva, cargando]);

  return (
    <Profundidad.Provider value={profundidad}>
      <Contexto.Provider value={valor}>
        <ThemeProvider theme={tema}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </Contexto.Provider>
    </Profundidad.Provider>
  );
}
